import { forwardRef, useImperativeHandle, useState } from 'react';
import { Elements, CardElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { Box, Typography } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';

import backendAxios from '../../utils/backendAxios';

// La instancia de Stripe se carga una sola vez (a nivel de módulo).
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

// Estilo del CardElement (su propio formato, no MUI). Coincide con la paleta del tema.
const CARD_OPTIONS = {
  style: {
    base: {
      fontSize: '16px',
      color: '#0E2465',
      fontFamily: 'inherit',
      '::placeholder': { color: '#94A3B8' },
    },
    invalid: { color: '#FF827A' },
  },
};

// Componente interno: aquí sí están disponibles los hooks de Stripe (dentro de <Elements>).
const CardInner = forwardRef((props, ref) => {
  const stripe = useStripe();
  const elements = useElements();
  const [error, setError] = useState('');
  const [listo, setListo] = useState(false);

  // BookingContainer llama a confirmar() al apretar "Confirmar reserva".
  // Pide un SetupIntent al backend, guarda/autentica la tarjeta y devuelve el paymentMethodId.
  useImperativeHandle(ref, () => ({
    async confirmar() {
      if (!stripe || !elements) throw new Error('El módulo de pagos aún se está cargando. Espera un momento.');
      const { data } = await backendAxios.post('/api/payments/stripe/setup-intent');
      const resultado = await stripe.confirmCardSetup(data.clientSecret, {
        payment_method: { card: elements.getElement(CardElement) },
      });
      if (resultado.error) {
        throw new Error(resultado.error.message || 'La tarjeta fue rechazada. Verifica los datos.');
      }
      return resultado.setupIntent.payment_method; // pm_...
    },
  }));

  return (
    <Box>
      <Box
        sx={{
          border: '1px solid',
          borderColor: error ? 'error.main' : '#E2E8F0',
          borderRadius: '10px',
          px: 1.75,
          py: 1.75,
          bgcolor: 'white',
          transition: 'border-color .15s',
          '&:focus-within': { borderColor: '#7495f8' },
        }}
      >
        <CardElement
          options={CARD_OPTIONS}
          onReady={() => setListo(true)}
          onChange={(e) => setError(e.error?.message || '')}
        />
      </Box>
      {error ? (
        <Typography variant="caption" color="error.main" sx={{ display: 'block', mt: 0.75, fontWeight: 600 }}>
          {error}
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
          <LockIcon sx={{ fontSize: 13, color: '#22C55E' }} />
          <Typography variant="caption" color="#737791">
            {listo ? 'Tu tarjeta se procesa de forma segura con Stripe.' : 'Cargando módulo de pago seguro…'}
          </Typography>
        </Box>
      )}
    </Box>
  );
});

// Envoltura con el provider <Elements>. Reenvía el ref al componente interno.
const StripeCard = forwardRef((props, ref) => (
  <Elements stripe={stripePromise}>
    <CardInner ref={ref} {...props} />
  </Elements>
));

export default StripeCard;
